import { faTools } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DialogContent, Grid, TextField,
 ToggleButton, ToggleButtonGroup
} from '@mui/material';

import {
  Datepicker, FlexContainer, SelectField, SelectLabel, SkeletonForm, TextError
} from 'components';
import { differenceInMonths } from 'date-fns';
import { ModalProps, ModalState } from 'hooks/ModalProvider';
import { EquipmentView } from 'models';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ToolsService } from 'services';
import shortid from 'shortid';
import { STOCK_ROLES_WITH_INTERVENTION_MANAGER, TOOLS_MONTHS_EXPIRATION_MAX, TOOL_TYPES } from 'utils/constants';
import {
  DocumentHelper,
  UserHelper,
  formatDate, parseDate, translate
} from 'utils/helpers';
import { ModalHeader } from './ModalHeader';
import { CustomActionButtons } from './_CustomActionButtons';

export const getDatabilanToolTypeOptions = () => [
  { value: TOOL_TYPES.LEAK_DETECTOR, label: translate('common.toolType.LEAK_DETECTOR') },
  { value: TOOL_TYPES.SCALE, label: translate('common.toolType.SCALE') }
];

export const AddToolModal = ({
                               onSubmit,
                               onClose,
                               defaultValues = new EquipmentView(),
                               forIntervention = false,
                               openSecondaryModal,
                               toolId,
                               withPicture = false
                             }: ModalProps<EquipmentView> & {
  defaultValues?: EquipmentView,
  forIntervention: boolean,
  openSecondaryModal: (options: ModalState) => void,
  toolId?: string,
  withPicture?: boolean
}) => {
  const [dateError, setDateError] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [picturesAdded, setPicturesAdded] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const {
 control, setValue, watch, handleSubmit
} = useForm<EquipmentView>({
    defaultValues
  });

  const formValues = watch();

  const canValidate = toolId && !formValues.validated && UserHelper.hasAccessRight(STOCK_ROLES_WITH_INTERVENTION_MANAGER);

  useEffect(() => {
    if (!toolId) {
      return;
    }
    setIsLoading(true);

    ToolsService.getTool(toolId)
        .then((resp) => {
          if (resp.documentList && resp.documentList.documents) {
            const pictures = resp.documentList.documents.map(document => ({
              name: document.name,
              base64Content: DocumentHelper.getDocumentWithBase64({ ...document, base64Content: document.content })
            }));
            setPicturesAdded(pictures);
          }

          // Set all form fields from response
          Object.entries(resp).forEach(([key, value]) => {
            setValue(key as any, value);
          });

          // Set address fields
          if (resp.address && typeof resp.address !== 'string') {
            setValue('address1', resp.address?.address1);
            setValue('address2', resp.address?.address2);
            setValue('city', resp.address?.city);
            setValue('country', resp.address?.country);
            setValue('postalCode', resp.address?.postalCode);
          }
        }).finally(() => setIsLoading(false));
  }, [toolId, setValue]);

  const handleAddPicture = event => (
      DocumentHelper.handleFileUpload({ callback: setPicturesAdded, callbackError: setUploadError, event })
  );

  const triggerConfirm = useCallback(() => {
    onSubmit({
      ...formValues,
      documentList: {
        documents: DocumentHelper.removeAllBase64Prefix(picturesAdded)
      }
    });
  }, [formValues, picturesAdded, onSubmit]);

  const isToolTooOld = (date: Date) => {
    const monthsOld = differenceInMonths(new Date(), new Date(date));
    return monthsOld > TOOLS_MONTHS_EXPIRATION_MAX;
  };

  const handleValidateModal = (data) => {
    if (!dateError) {
      if (isToolTooOld(parseDate(data.lastControlDate))) {
        openSecondaryModal({
          type: 'WARNING',
          text: translate('warnings.toolList.toolTooOld'),
          buttonCancel: translate('button.cancel'),
          buttonConfirm: translate('button.confirmAndCreate'),
          onConfirm: () => triggerConfirm()
        });
      } else {
        triggerConfirm();
      }
    }
  };

  return (
    <>
      <form autoComplete="off" onSubmit={handleSubmit(handleValidateModal)}>
        <ModalHeader onClose={onClose}>
          <FontAwesomeIcon icon={faTools} />
          {(toolId || forIntervention) ? translate('modalTool.editTool') : translate('modalTool.newTool')}
        </ModalHeader>

        {isLoading ? (
          <SkeletonForm />
          ) : (
            <>
              <DialogContent style={{ width: '400px', padding: '10px' }}>
                <FlexContainer flexDirection="column">
                  {toolId && (
                    <ToggleButtonGroup
                            aria-label={translate('modalTool.toolStatus')}
                            exclusive
                            size="small"
                            style={{ marginBottom: '2rem' }}
                            value={formValues.activated}
                            onChange={(event, value) => {
                              if (value === null) return;
                              setValue('activated', value);
                            }}
                        >
                      <ToggleButton aria-label={translate('common.activated')} value={true}>
                        {translate('common.activated')}
                      </ToggleButton>
                      <ToggleButton aria-label={translate('common.deactivated')} value={false}>
                        {translate('common.deactivated')}
                      </ToggleButton>
                    </ToggleButtonGroup>
                    )}

                  <Grid container direction="column" spacing={1}>
                    <Grid item>
                      <Controller
                            control={control}
                            name="type"
                            render={({ field }) => (
                              <SelectField
                                    {...field}
                                    id="toolType"
                                    isDisabled={!!toolId || forIntervention}
                                    label="common.toolTypes"
                                    name="legalStatus"
                                    options={getDatabilanToolTypeOptions()}
                                    required
                                />
                            )}
                        />
                      <Controller
                            control={control}
                            name="brand"
                            render={({ field, fieldState }) => (
                              <TextField
                                    {...field}
                                    error={!!fieldState.error}
                                    fullWidth
                                    label={translate('common.brand')}
                                />
                            )}
                        />
                      <Controller
                            control={control}
                            name="model"
                            render={({ field, fieldState }) => (
                              <TextField
                                    {...field}
                                    error={!!fieldState.error}
                                    fullWidth
                                    label={translate('common.model')}
                                    required
                                />
                            )}
                        />
                    </Grid>
                    <Grid item>
                      <Controller
                            control={control}
                            name="serialNumber"
                            render={({ field, fieldState }) => (
                              <TextField
                                    {...field}
                                    error={!!fieldState.error}
                                    fullWidth
                                    label={translate('common.serialNumber')}
                                    required
                                />
                            )}
                        />
                    </Grid>
                    <Grid item>
                      <Controller
                            control={control}
                            name="internalIdentification"
                            render={({ field, fieldState }) => (
                              <TextField
                                    {...field}
                                    error={!!fieldState.error}
                                    fullWidth
                                    label={translate('common.internalIdentification')}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item>
                      <Controller
                            control={control}
                            name="lastControlDate"
                            render={({ field }) => (
                              <Datepicker
                                    {...field}
                                    disableFuture
                                    error={Boolean(dateError)}
                                    label="common.controlDate"
                                    maxDate={formatDate(new Date())}
                                    maxDateMessage="modalTool.errorMaxDate"
                                    required
                                    onError={error => setDateError(error)}
                                />
                            )}
                        />
                    </Grid>
                    {withPicture && (
                      <Grid item>
                        <SelectLabel>{translate('modalTool.toolPicture')}</SelectLabel>
                        <input
                                accept="image/*"
                                placeholder={translate('modalTool.toolPictureAdd')}
                                type="file"
                                onChange={handleAddPicture}
                            />
                        {uploadError && <TextError>{uploadError}</TextError>}
                        {picturesAdded && picturesAdded.length > 0 && (
                        <FlexContainer className="modalPicturesContainer">
                          {picturesAdded.map(picture => (
                            <img alt={picture.name} key={shortid.generate()} src={picture.base64Content} />
                                  ))}
                        </FlexContainer>
                            )}
                      </Grid>
                      )}
                  </Grid>
                </FlexContainer>
              </DialogContent>

              <CustomActionButtons
                    forValidation={canValidate}
                    isLoading={isLoading}
                    onClose={onClose}
                />
            </>
          )}
      </form>
    </>
  );
};
